import { Accept } from "react-dropzone";

import bwImage from "@/assets/reference-types/bw.png";
import cannyImage from "@/assets/reference-types/canny.png";
import colorImage from "@/assets/reference-types/color.png";
import faceImage from "@/assets/reference-types/face.png";
import initImage from "@/assets/reference-types/init.png";
import lineartImage from "@/assets/reference-types/lineart.png";
import poseImage from "@/assets/reference-types/pose.png";
import segmentationImage from "@/assets/reference-types/segmentation.png";
import structureImage from "@/assets/reference-types/structure.png";
import styleImage from "@/assets/reference-types/style.png";
import { GuidanceFileType } from "@/components/graphql/schema.graphql";

import { ReferenceMetadata } from "./types";

export const ACCEPTED_REFERENCE_IMAGE_TYPES: Accept = {
  "image/jpeg": [".jpeg", ".jpg"],
  "image/png": [".png"],
  "image/webp": [".webp"],
  "image/gif": [".gif"],
};

export const REFERENCES_METADATA: Partial<{
  [K in GuidanceFileType]: ReferenceMetadata;
}> = {
  INIT: {
    label: "Reference Image",
    incompatibleReferenceTypes: ["COLOR_SKETCH"],
    referenceType: "INIT",
    preprocess: false,
    weight: 0.5,
    description: "Use this image as a starting point and create variations.",
    imageSrc: initImage,
    isLocalized: true,
  },
  PROMPT: {
    label: "Style Reference",
    incompatibleReferenceTypes: ["FACE"],
    referenceType: "PROMPT",
    preprocess: false,
    weight: 1,
    description: "Transfer the style of an image onto your generations.",
    imageSrc: styleImage,
    allowMultiple: true,
    isLocalized: false,
  },
  COLOR_SKETCH: {
    label: "Color Sketch",
    incompatibleReferenceTypes: [
      "PROMPT",
      "CANNY",
      "SCRIBBLE",
      "LINEART",
      "INIT",
    ],
    referenceType: "COLOR_SKETCH",
    preprocess: false,
    weight: 1,
    description:
      "Upload or draw a color sketch to guide your image generation.",
    imageSrc: colorImage,
    isLocalized: true,
    isDeprecated: true,
  },
  SCRIBBLE: {
    label: "B&W Sketch",
    incompatibleReferenceTypes: ["COLOR_SKETCH", "CANNY", "LINEART"],
    referenceType: "SCRIBBLE",
    preprocess: false,
    weight: 1,
    description:
      "Upload or draw a black and white sketch to guide your image generation.",
    imageSrc: bwImage,
    isLocalized: true,
    isDeprecated: true,
  },
  FACE: {
    label: "Character Face",
    incompatibleReferenceTypes: ["PROMPT"],
    referenceType: "FACE",
    preprocess: false,
    weight: 1,
    description:
      "Upload a close-up image of a character's face to generate images with this face.",
    imageSrc: faceImage,
    isLocalized: false,
    isDeprecated: true,
  },
  POSE: {
    label: "Character Pose",
    incompatibleReferenceTypes: [],
    referenceType: "POSE",
    preprocess: true,
    weight: 0.9, // recommended default conditioning scale is 0.9
    description:
      "Generate an image in a specific human pose that matches the pose of the person in your image.",
    imageSrc: poseImage,
    isLocalized: true,
  },
  DEPTH: {
    label: "Structure",
    incompatibleReferenceTypes: [],
    referenceType: "DEPTH",
    preprocess: true,
    weight: 0.8, // recommended default conditioning scale is 0.8
    description:
      "Change the appearance of your asset while maintaining its base structure.",
    imageSrc: structureImage,
    isLocalized: true,
  },
  CANNY: {
    label: "Outline",
    incompatibleReferenceTypes: ["COLOR_SKETCH", "SCRIBBLE", "LINEART"],
    referenceType: "CANNY",
    preprocess: true,
    weight: 0.7, // recommended default conditioning scale is 0.7
    description:
      "Follow the outlines of your reference image to guide your generation — great for structured concepts. This uses Canny Edge detection to extract key shapes.",
    imageSrc: cannyImage,
    isLocalized: true,
  },
  LINEART: {
    label: "Line Art",
    incompatibleReferenceTypes: ["COLOR_SKETCH", "SCRIBBLE", "CANNY"],
    referenceType: "LINEART",
    preprocess: true,
    weight: 0.7, // recommended default conditioning scale is 0.7
    description:
      "Follow your black and white sketch or line art to guide your generation. This uses your drawing directly without preprocessing.",
    imageSrc: lineartImage,
    isLocalized: true,
  },
  SEGMENTATION: {
    label: "Segmentation",
    incompatibleReferenceTypes: [],
    referenceType: "SEGMENTATION",
    preprocess: true,
    weight: 1,
    description: "Generate an image keeping the same segments of your image.",
    imageSrc: segmentationImage,
    isLocalized: true,
    isDeprecated: true,
  },
} as const;
